import React, { useMemo, useImperativeHandle, forwardRef } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  GridOptions,
  ModuleRegistry,
  AllCommunityModule,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useTheme } from '../../../hooks/useTheme';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface Subscription {
  id: number;
  email: string;
  name?: string;
  subscription_plan: string;
  subscription_status: string;
  created_at: string;
}

interface SubscriptionsGridProps {
  subscriptions: Subscription[];
  searchValue?: string;
}

const SubscriptionsGrid = forwardRef<any, SubscriptionsGridProps>(
  ({ subscriptions, searchValue = '' }, ref) => {
    const { colors } = useTheme();
    const gridRef = React.useRef<AgGridReact>(null);
    const gridApiRef = React.useRef<any>(null);

    // Only render on web
    if (Platform.OS !== 'web') {
      return null;
    }

    // Filter subscriptions based on search value
    const filteredSubscriptions = React.useMemo(() => {
      if (!searchValue.trim()) return subscriptions;
      const lowerSearch = searchValue.toLowerCase();
      return subscriptions.filter((sub) => {
        const searchFields = [
          sub.email,
          sub.name,
          sub.subscription_plan,
          sub.subscription_status,
          sub.id?.toString(),
        ];
        return searchFields.some(
          (field) => field && String(field).toLowerCase().includes(lowerSearch)
        );
      });
    }, [subscriptions, searchValue]);

    const columnDefs: ColDef[] = useMemo(
      () => [
        { field: 'id', headerName: 'ID', width: 80, sortable: true },
        {
          field: 'email',
          headerName: 'Email',
          flex: 1,
          sortable: true,
          filter: true,
        },
        {
          field: 'name',
          headerName: 'Name',
          flex: 1,
          sortable: true,
          filter: true,
        },
        {
          field: 'subscription_plan',
          headerName: 'Plan',
          width: 150,
          sortable: true,
          filter: true,
        },
        {
          field: 'subscription_status',
          headerName: 'Status',
          width: 120,
          sortable: true,
          filter: true,
        },
        {
          field: 'created_at',
          headerName: 'Created',
          width: 180,
          sortable: true,
          valueFormatter: (params) => {
            if (!params.value) return '';
            return new Date(params.value).toLocaleString();
          },
        },
      ],
      []
    );

    const gridOptions: GridOptions = useMemo(
      () => ({
        theme: 'legacy',
        defaultColDef: {
          resizable: true,
          sortable: true,
          filter: true,
        },
        pagination: false,
        rowHeight: 50,
        headerHeight: 40,
        suppressCellFocus: true,
        animateRows: true,
        suppressPaginationPanel: true,
        onGridReady: (params) => {
          gridApiRef.current = params.api;
        },
      }),
      []
    );

    // No need for useEffect - filtering is done via filteredSubscriptions

    useImperativeHandle(ref, () => ({
      getApi: () => gridRef.current?.api,
    }));

    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <div
          className="ag-theme-alpine"
          style={
            {
              height: 'calc(100vh - 200px)',
              width: '100%',
              overflow: 'hidden',
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              '--ag-header-background-color': colors.card,
              '--ag-background-color': colors.background,
              '--ag-odd-row-background-color': colors.card,
              '--ag-row-hover-color': colors.card,
              '--ag-header-foreground-color': colors.text,
              '--ag-foreground-color': colors.text,
              '--ag-border-color': colors.border,
              '--ag-secondary-border-color': colors.border,
              '--ag-input-focus-border-color': colors.primary,
              '--ag-selected-row-background-color': `${colors.primary}20`,
              '--ag-range-selection-background-color': `${colors.primary}15`,
              '--ag-range-selection-border-color': colors.primary,
              '--ag-checkbox-checked-color': colors.primary,
              '--ag-checkbox-unchecked-color': colors.border,
              '--ag-input-disabled-background-color': colors.card,
              '--ag-input-disabled-border-color': colors.border,
              '--ag-input-disabled-foreground-color': colors.secondary,
              '--ag-panel-background-color': colors.card,
              '--ag-tool-panel-background-color': colors.card,
              '--ag-side-button-selected-background-color': colors.primary,
              '--ag-side-button-selected-foreground-color': '#ffffff',
            } as React.CSSProperties & { [key: string]: any }
          }
        >
          <style>{`
          .ag-theme-alpine .ag-body-viewport::-webkit-scrollbar,
          .ag-theme-alpine .ag-body-horizontal-scroll::-webkit-scrollbar {
            display: none;
          }
          .ag-theme-alpine .ag-body-viewport,
          .ag-theme-alpine .ag-body-horizontal-scroll {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .ag-theme-alpine {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
            font-size: 12px;
          }
          .ag-theme-alpine .ag-header {
            border-bottom: 1px solid ${colors.border};
            font-size: 13px;
            font-weight: 600;
          }
          .ag-theme-alpine .ag-header-cell {
            border-right: 1px solid ${colors.border};
            font-size: 13px;
            font-weight: 600;
          }
          .ag-theme-alpine .ag-header-cell-text {
            font-size: 13px;
            font-weight: 600;
          }
          .ag-theme-alpine .ag-row {
            border-bottom: 1px solid ${colors.border};
            font-size: 12px;
            font-weight: 400;
          }
          .ag-theme-alpine .ag-cell {
            border-right: 1px solid ${colors.border};
            font-size: 12px;
            font-weight: 400;
            line-height: 1.5;
          }
          .ag-theme-alpine .ag-cell-value {
            font-size: 12px;
            font-weight: 400;
          }
          .ag-theme-alpine input,
          .ag-theme-alpine select,
          .ag-theme-alpine button {
            font-size: 12px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
          }
        `}</style>
          <AgGridReact
            ref={gridRef}
            rowData={filteredSubscriptions}
            columnDefs={columnDefs}
            gridOptions={gridOptions}
            domLayout="normal"
          />
        </div>
      </View>
    );
  }
);

SubscriptionsGrid.displayName = 'SubscriptionsGrid';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
  },
});

export default SubscriptionsGrid;
